import { getNestedValue, setNestedValue } from 'helper-functions/lodash/lodash-subs.js'
import { formatAnimalIds, reverseFormatAnimalIds } from 'features-v2/animal-management/components/helpers/animal-ids-utils.js'

function compare(left, operator, right) {
    switch (operator) {
        case '>':
            return left > right
        case '>=':
            return left >= right
        case '<':
            return left < right
        case '<=':
            return left <= right
        case '===':
            return left === right
        case '!==':
            return left !== right
        case 'includes':
            return Array.isArray(left) ? left.includes(right) : String(left ?? '').includes(right)
        default:
            throw new Error(`Unsupported operator: ${operator}`)
    }
}

function getPathValues(obj, path) {
    const parts = path.split('.')
    let current = [obj]

    for (const part of parts) {
        const next = []

        for (const item of current) {
            if (item == null) continue

            if (part === '[]') {
                if (Array.isArray(item)) {
                    next.push(...item)
                }
            } else {
                next.push(item[part])
            }
        }

        current = next
    }

    return current.filter(v => v !== undefined)
}

function buildCounts(items, field) {
    const counts = new Map()

    for (const item of items) {
        const values = getPathValues(item, field)
        for (const value of values) {
            counts.set(value, (counts.get(value) || 0) + 1)
        }
    }

    return counts
}

function matchValues(values, operator, expected, quantifier = 'some') {
    if (!values.length) return false

    if (quantifier === 'every') {
        return values.every(value => compare(value, operator, expected))
    }

    return values.some(value => compare(value, operator, expected))
}

function matchesRule(item, rule, countsByField) {
    if (rule.op && Array.isArray(rule.rules)) {
        if (rule.op === 'and') {
            return rule.rules.every(child => matchesRule(item, child, countsByField))
        }

        if (rule.op === 'or') {
            return rule.rules.some(child => matchesRule(item, child, countsByField))
        }

        throw new Error(`Unsupported group op: ${rule.op}`)
    }

    if (rule.type === 'value') {
        const quantifier = rule.quantifier || 'some'
        const values = getPathValues(item, rule.field)
        return matchValues(values, rule.operator, rule.value, quantifier)
    }

    if (rule.type === 'count') {
        const quantifier = rule.quantifier || 'some'
        const fieldCounts = countsByField.get(rule.field)
        const values = getPathValues(item, rule.field)
        const counts = values.map(v => fieldCounts.get(v) || 0)
        return matchValues(counts, rule.operator, rule.value, quantifier)
    }

    if (rule.type === 'summaryMultiOption') {
        const summary = getNestedValue(item, rule.field || 'detail.summary') || {}
        if (!summary || typeof summary !== 'object') return false
        const exclude = Array.isArray(rule.excludeKeys) ? rule.excludeKeys : []
        const hasMulti = Object.entries(summary).some(([key, vals]) => {
            if (exclude.some(ex => key.includes(ex))) return false
            return Array.isArray(vals) && vals.length > 1
        })
        return rule.operator === '===' ? hasMulti === rule.value : hasMulti
    }

    throw new Error(`Unsupported rule type: ${rule.type}`)
}

function collectCountFields(rule, fields = new Set()) {
    if (rule.op && Array.isArray(rule.rules)) {
        for (const child of rule.rules) {
            collectCountFields(child, fields)
        }
        return fields
    }

    if (rule.type === 'count') {
        fields.add(rule.field)
    }

    return fields
}

function pickFields(item, fieldKeys) {
    if (!Array.isArray(fieldKeys) || fieldKeys.length === 0) {
        return item
    }

    return fieldKeys.reduce((acc, key) => {
        if (key in item) {
            acc[key] = item[key]
        }
        return acc
    }, {})
}

export function filterGroups(items, criteria, fieldKeys) {
    const countFields = [...collectCountFields(criteria)]
    const countsByField = new Map()
    for (const field of countFields) {
        countsByField.set(field, buildCounts(items, field))
    }
    const filteredItems = items.filter(item => matchesRule(item, criteria, countsByField))
    const mappedItems = filteredItems.map(item => {
        const picked = fieldKeys ? pickFields(item, fieldKeys) : item
        return { ...picked, species: item?.animals?.[0]?.detail?.species ?? null }
    })
    return mappedItems
}

export function buildMovementLegs({ animalsToMove = [], group = {}, paddockId: toPaddockId = null, farmId: toFarmId = null, customLocation: toCustomLocation = null, paddockLookup } = {}) {
    // console.log(animalsToMove, group, toPaddockId, toFarmId, toCustomLocation)
    if (!Array.isArray(animalsToMove) || !Array.isArray(group?.animals) || (toPaddockId == null && toCustomLocation == null)) return null

    const moveSet = new Set(animalsToMove)
    const fromMap = new Map()

    for (const animal of group.animals) {
        const matchedIds = Array.isArray(animal?.aIds) ? animal.aIds.filter(id => moveSet.has(id)) : []
        if (!matchedIds.length) continue
        const fromPaddockId = animal?.paddockId ?? null
        const fromCustomLocation = animal?.detail?.location?.customLocation ?? null
        let fromFarmId = animal?.farmId ?? null
        // check it aligns from paddockLookup
        if (paddockLookup && fromPaddockId) {
            const paddock = paddockLookup[fromPaddockId]
            if (paddock && paddock?.farmId !== fromFarmId) {
                fromFarmId = paddock?.farmId
            }
        }

        const key = `${fromFarmId}|${fromPaddockId}`

        if (!fromMap.has(key)) {
            fromMap.set(key, {
                animalIds: [],
                farmId: fromFarmId,
                ...(fromPaddockId != null ? { paddockId: fromPaddockId } : {}),
                ...(fromCustomLocation != null ? { customLocation: fromCustomLocation } : {}),
            })
        }

        fromMap.get(key).animalIds.push(...matchedIds)
    }

    const from = [...fromMap.values()]
        .map(x => ({
            ...x,
            animalIds: formatAnimalIds([...new Set(x.animalIds)]),
        }))
        .filter(x => x.animalIds.length) /// xxx

    if (!from.length) return null

    return {
        from,
        to: {
            farmId: toFarmId ?? from[0]?.farmId ?? null,
            ...(toPaddockId != null ? { paddockId: toPaddockId } : {}),
            ...(toCustomLocation != null ? { customLocation: toCustomLocation } : {}),
        },
    }
}

function getMovementLegRefs(legs = {}) {
    return {
        farmIds: [...new Set([...(legs?.from?.map(x => x?.farmId).filter(id => id != null) || []), ...(legs?.to?.farmId != null ? [legs.to.farmId] : [])])],
        paddockIds: [...new Set([...(legs?.from?.map(x => x?.paddockId).filter(id => id != null) || []), ...(legs?.to?.paddockId != null ? [legs.to.paddockId] : [])])],
    }
}

export function legsToEvents({ paddockId = null, filteredAnimalIds, group, farmId, paddockLookup, customLocation } = {}) {
    const legs = paddockId || customLocation ? buildMovementLegs({ animalsToMove: filteredAnimalIds, group, paddockId, farmId, paddockLookup, customLocation }) : null
    const legRefs = paddockId ? getMovementLegRefs(legs) : null
    return {
        legs,
        legRefs,
    }
}
